import {
	ButtonGroup,
	IPageRange,
	Loading,
	PageTurnOptions,
	PagingOptions,
	Pullout,
	PulloutType,
	SearchBox,
	Select,
	Switch,
	ToolMenu,
	ToolMenuContent,
} from '@aon/aon-tws-common-ui';
import * as React from 'react';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components';
import { DuplicateEmailsWarning } from '../../../components/common/warnings';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import {
	AudienceSourceType,
	DistributionContactsListSourceType,
	DistributionType,
	IColumn,
	IDistributionContactActionCreators,
	ISurveyParticipantActionCreators,
	SurveyParticipantsMenuActions,
	SurveyStatusType,
} from '../../../models';
import { IDistributionContactState } from '../../../store/DistributionContactStore';
import { GetSurveyParticipantActionMenuItems, ISurveyParticipantState } from '../../../store/SurveyParticipantStore';
import { BrowserHistoryHelper, ColumnHelper } from '../../../utilities';
import { DistributionHelper } from '../../../utilities/DistributionHelper';
import { LoadingWrapper, NoSearchResultsMessage, WarningModal } from '../../common';
import { DistributionContactsTable } from './DistributionContactsTable';
import { SurveyParticipantActionWarningModal } from './SurveyParticipantActionWarningModal';

export type IDistributionContactsListProps = {
	surveyId: number;
	audienceId: number;
	readonly?: boolean;
	surveyStatus?: SurveyStatusType;
	distributionContactsListSourceType: DistributionContactsListSourceType;
	audienceSourceType?: AudienceSourceType;
} & IDistributionContactState &
	ISurveyParticipantState &
	IDistributionContactActionCreators &
	ISurveyParticipantActionCreators;

const Content = styled.div`
	width: 100%;
`;
Content.displayName = 'Content';
const MenuContainer = styled.div`
	margin-bottom: 30px;
`;
MenuContainer.displayName = 'MenuContainer';
export const ActionMenu = styled(Select)``;
ActionMenu.displayName = 'ActionMenu';

export class DistributionContactsList extends React.Component<IDistributionContactsListProps> {
	constructor(props: IDistributionContactsListProps) {
		super(props);

		this.getResultsCount = this.getResultsCount.bind(this);
		this.updatePaging = this.updatePaging.bind(this);
		this.updateSearchText = this.updateSearchText.bind(this);
		this.sortOnColumn = this.sortOnColumn.bind(this);
		this.updateColumns = this.updateColumns.bind(this);
		this.setDisplayDuplicateEmailsState = this.setDisplayDuplicateEmailsState.bind(this);
	}

	public componentDidMount() {
		this.props.GetColumns(
			this.props.distributionContactsListSourceType,
			this.props.audienceId,
			this.props.surveyStatus!,
			this.props.distributionType!,
		);
		if (this.props.surveyId && this.props.audienceId) {
			const showOnlyDuplicateEmails = this.getshowDuplicatesOnlyQueryStringValue();
			this.props.SetDisplayDuplicateEmailsState(showOnlyDuplicateEmails);
			this.getDistributionContacts(
				this.props.pageRange,
				this.props.searchText,
				this.props.sortParams.sortedBy!,
				this.props.sortParams.sortAsc!,
				this.props.columns,
				showOnlyDuplicateEmails,
			);
			// This is to check if there are any duplicates so passing in getOnlyDuplicates = true
			this.props.GetSurveyParticipantsCount(
				this.props.surveyId,
				this.props.audienceId,
				'', // Sending this empty since we need to check the duplicates across DL
				true,
			);
		}
	}

	public componentDidUpdate(previousProps: IDistributionContactsListProps) {
		if (
			this.props.displayDuplicateEmailAddresses !== previousProps.displayDuplicateEmailAddresses ||
			this.props.audienceId !== previousProps.audienceId
		) {
			const { pageRange, searchText, sortParams, columns, displayDuplicateEmailAddresses } = this.props;
			this.getDistributionContacts(
				pageRange,
				searchText,
				sortParams.sortedBy!,
				sortParams.sortAsc!,
				columns,
				displayDuplicateEmailAddresses,
			);
		}
	}

	public componentWillUnmount() {
		this.props.SetDisplayDuplicateEmailsState(false);
	}

	public render() {
		const {
			distributionContacts,
			distributionType,
			pageRange,
			readonly,
			searchText,
			selectedDistributionContacts,
			sortParams,
			distributionContactsListSourceType,
			surveyStatus,
			audienceSourceType,
			displayDuplicateEmailAddresses,
			contactsCount,
		} = this.props;
		const columns =
			readonly || distributionContactsListSourceType === DistributionContactsListSourceType.ResetResendReopen
				? this.props.columns.filter((column: IColumn) => column.name !== 'actions')
				: this.props.columns;

		return (
			<>
				<DuplicateEmailsWarning duplicateContactsCount={contactsCount} surveyStatus={surveyStatus!} distributionType={distributionType!} />
				{!readonly &&
					(surveyStatus === SurveyStatusType.Scheduled || surveyStatus === SurveyStatusType.SurveyInProgress) &&
					audienceSourceType !== AudienceSourceType.Hris && (
						<>
							<MenuContainer className={'tws-action-menu-container'}>
								<ActionMenu
									items={this.getActionMenuItems()}
									size='lg'
									selectSize='lg'
									dropdownSize='lg'
									disabled={!selectedDistributionContacts || selectedDistributionContacts.length === 0}
									keepDisplayText={true}
									displayText={ContentConstants.ACTIONMENU_SELECT_ACTIONS}
									onApply={this.handleAction}
								/>
							</MenuContainer>

							{!readonly &&
								DistributionHelper.isNotAnonymous(distributionType) &&
								distributionContactsListSourceType === DistributionContactsListSourceType.ResetResendReopen && (
									<MenuContainer className={'tws-action-warning-container'}>
										<Pullout type={PulloutType.Note}>{HtmlParser(ContentConstants.SURVEY_PARTICIPANTS_ACTION_WARNING)}</Pullout>
									</MenuContainer>
								)}
						</>
					)}
				<PagingOptions pageRange={pageRange} updatePageRange={this.updatePaging} />
				<ToolMenu openByDefault={this.getshowDuplicatesOnlyQueryStringValue()}>
					<ToolMenuContent type={'right'}>
						<SearchBox
							callbackFunc={this.updateSearchText}
							resultsCount={this.getResultsCount()}
							initialSearchText={searchText}
							inputSize={'sm'}
						/>
					</ToolMenuContent>
					<ToolMenuContent type={'left'}>
						<ButtonGroup type='vertical'>
							<Select
								items={columns}
								multiple={true}
								selectedValues={ColumnHelper.GetSelectedColumnNames(columns)}
								showCount={true}
								displayText={ContentConstants.COLUMNPICKER_TITLE}
								onApply={this.updateColumns}
								selectWidth='180px'
								optionsContainerWidth='250px'
								size='sm'
							/>
						</ButtonGroup>
					</ToolMenuContent>
					{(surveyStatus === SurveyStatusType.Scheduled ||
						surveyStatus === SurveyStatusType.SurveyInProgress ||
						surveyStatus === SurveyStatusType.SurveyClosed) && (
						<ToolMenuContent type={'bottom'}>
							<Switch
								text={ContentConstants.DISTRIBUTION_CONTACTS_SHOW_DUPLICATE_EMAILS_TOGGLE_LABEL}
								checked={displayDuplicateEmailAddresses}
								getValue={this.setDisplayDuplicateEmailsState}
							/>
						</ToolMenuContent>
					)}
				</ToolMenu>
				{this.props.loadingContacts && (
					<LoadingWrapper>
						<Loading height='70px' width='70px' />
					</LoadingWrapper>
				)}
				{!this.props.loadingContacts && (
					<DistributionContactsTable
						{...this.props}
						searchText={searchText}
						readonly={readonly}
						distributionContacts={distributionContacts}
						distributionType={distributionType}
						columns={columns}
						sortParams={sortParams}
						handleSort={this.sortOnColumn}
						toggleCheckboxes={this.props.ToggleCheckboxes}
						onDelete={(surveyId: number, audienceId: number, distributionContactId: number) =>
							this.delete(surveyId, audienceId, [distributionContactId])
						}
					/>
				)}
				{!distributionContacts.length && searchText && <NoSearchResultsMessage />}
				<PageTurnOptions pageRange={pageRange} updatePageRange={this.updatePaging} />

				{this.renderModals()}
			</>
		);
	}

	public renderModals = () => {
		const {
			audienceId,
			distributionType,
			isReopening,
			isDeletingDistributionContacts,
			isReopenWarningModalOpen,
			isResendingEmail,
			isResetting,
			isResetWarningModalOpen,
			selectedDistributionContacts,
			surveyId,
			ToggleReopenWarningModal,
			ToggleResetWarningModal,
		} = this.props;

		const deleteParticipantsContentBody =
			distributionType === DistributionType.SingleLink
				? ContentConstants.WARNING_DELETE_PARTICIPANTS_SINGLELINK_BODY
				: ContentConstants.WARNING_DELETE_PARTICIPANTS_BODY;
		return (
			<>
				{this.props.isResendWarningModalOpen && (
					<WarningModal
						isOpen={this.props.isResendWarningModalOpen}
						contentHeadline={ContentConstants.WARNING_MODAL_RESEND_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_RESEND_BODY.replace('{total}', selectedDistributionContacts.length.toString())}
						secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
						onClose={() => this.props.ToggleResendWarningModal(false)}
						onSubmit={this.handleResend}
						primaryButtonDisabled={isResendingEmail}
					/>
				)}

				{this.props.isDeleteDistributionContactsModalOpen && (
					<WarningModal
						isOpen={this.props.isDeleteDistributionContactsModalOpen}
						contentHeadline={ContentConstants.WARNING_MODAL_DELETE_HEADLINE}
						contentBody={
							<>{HtmlParser(deleteParticipantsContentBody.replace('{total}', selectedDistributionContacts.length.toString()))}</>
						}
						secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
						onClose={() => this.props.ToggleDeleteDistributionContactsWarningModal(false)}
						onSubmit={this.handleDelete}
						primaryButtonDisabled={isDeletingDistributionContacts}
					/>
				)}

				{isReopenWarningModalOpen && (
					<SurveyParticipantActionWarningModal
						isOpen={isReopenWarningModalOpen}
						audienceId={audienceId}
						surveyId={surveyId}
						contentBody={ContentConstants.WARNING_MODAL_REOPEN_BODY}
						headline={ContentConstants.WARNING_MODAL_REOPEN_HEADLINE}
						isLoading={isReopening}
						isResend={this.props.isReopenAndResend}
						resendLabel={ContentConstants.SURVEY_PARTICIPANT_REOPEN_RESEND_CHECKBOX_LABEL}
						selectedDistributionContacts={selectedDistributionContacts}
						toggleWarningModal={ToggleReopenWarningModal}
						toggleResendCheckbox={this.props.ToggleReopenResendCheckbox}
						onSubmit={this.props.Reopen}
					/>
				)}

				{isResetWarningModalOpen && (
					<SurveyParticipantActionWarningModal
						isOpen={isResetWarningModalOpen}
						audienceId={audienceId}
						surveyId={surveyId}
						contentBody={ContentConstants.WARNING_MODAL_RESET_BODY}
						headline={ContentConstants.WARNING_MODAL_RESET_HEADLINE}
						isLoading={isResetting}
						isResend={this.props.isResetAndResend}
						resendLabel={ContentConstants.SURVEY_PARTICIPANT_RESET_RESEND_CHECKBOX_LABEL}
						selectedDistributionContacts={selectedDistributionContacts}
						toggleWarningModal={ToggleResetWarningModal}
						toggleResendCheckbox={this.props.ToggleResetResendCheckbox}
						onSubmit={this.props.Reset}
					/>
				)}
			</>
		);
	};

	public getActionMenuItems = () => {
		const { distributionType, selectedDistributionContacts, distributionContactsListSourceType } = this.props;

		return GetSurveyParticipantActionMenuItems(distributionContactsListSourceType, distributionType!, selectedDistributionContacts);
	};

	public delete = (surveyId: number, audienceId: number, distributionContactIds: number[]) => {
		const { surveyStatus, DeleteDistributionContacts, DeleteSurveyParticipants } = this.props;
		// Need to make different API calls for Survey Distribution and Survey Admin
		return surveyStatus === SurveyStatusType.Scheduled || surveyStatus === SurveyStatusType.SurveyInProgress
			? DeleteSurveyParticipants(surveyId, audienceId, distributionContactIds)
			: DeleteDistributionContacts(surveyId, audienceId, distributionContactIds);
	};

	public getDistributionContacts(
		pageRange: IPageRange,
		searchText: string,
		sortedBy: string,
		sortAsc: boolean,
		selectedColumns?: IColumn[],
		showDuplicateEmailAddresses?: boolean,
	) {
		const {
			surveyId,
			audienceId,
			distributionContactsListSourceType,
			selectedDistributionContacts,
			GetDistributionContacts,
			GetPagedSurveyParticipants,
			surveyStatus,
		} = this.props;
		const selectedDistributionContactIds = selectedDistributionContacts
			? selectedDistributionContacts.map((contact) => {
					return contact.distributionContactId;
			  })
			: undefined;
		// Since this component is used in both Survey Distribution and Survey Admin,
		// Need to make different API calls for both sides in order to show survey completion status for each survey
		// participant in Survey Admin
		return surveyStatus === SurveyStatusType.Scheduled ||
			surveyStatus === SurveyStatusType.SurveyInProgress ||
			surveyStatus === SurveyStatusType.SurveyClosed ||
			distributionContactsListSourceType === DistributionContactsListSourceType.ResetResendReopen
			? GetPagedSurveyParticipants(
					surveyId,
					audienceId,
					pageRange,
					searchText,
					sortedBy,
					sortAsc,
					selectedColumns,
					selectedDistributionContactIds,
					showDuplicateEmailAddresses,
			  )
			: GetDistributionContacts(surveyId, audienceId, pageRange, searchText, sortedBy, sortAsc, selectedColumns);
	}

	public updatePaging(pageRange: IPageRange) {
		this.getDistributionContacts(
			pageRange,
			this.props.searchText,
			this.props.sortParams.sortedBy!,
			this.props.sortParams.sortAsc!,
			this.props.columns,
		);
	}

	public updateSearchText(searchText: string) {
		if (searchText !== this.props.searchText) {
			const pageRange: IPageRange = {
				...this.props.pageRange,
				offset: 0,
			};
			this.getDistributionContacts(
				pageRange,
				searchText,
				this.props.sortParams.sortedBy!,
				this.props.sortParams.sortAsc!,
				this.props.columns,
				this.props.displayDuplicateEmailAddresses,
			);
		}
	}

	public sortOnColumn(sortedBy: string, sortAscByDefault: boolean) {
		let sortAsc = !this.props.sortParams.sortAsc;
		if (this.props.sortParams.sortedBy !== sortedBy) {
			sortAsc = sortAscByDefault;
		}

		this.getDistributionContacts(
			this.props.pageRange,
			this.props.searchText,
			sortedBy,
			sortAsc,
			this.props.columns,
			this.props.displayDuplicateEmailAddresses,
		);
	}

	public getResultsCount() {
		if (this.props.loadingContacts) {
			return undefined;
		}
		return this.props.pageRange.total;
	}

	public updateColumns(values?: any) {
		const columns = ColumnHelper.UpdateSelectedColumns(this.props.columns, values);

		this.getDistributionContacts(
			this.props.pageRange,
			this.props.searchText,
			this.props.sortParams.sortedBy || 'CreatedOn',
			this.props.sortParams.sortAsc === true,
			columns,
			this.props.displayDuplicateEmailAddresses,
		);
	}

	public handleResend = () => {
		const { audienceId, selectedDistributionContacts, surveyId, Resend } = this.props;

		const selectedDistributionContactIds = selectedDistributionContacts.map(
			(distributionContact) => distributionContact.distributionContactId,
		);
		Resend(surveyId, audienceId, selectedDistributionContactIds);
	};

	public handleDelete = () => {
		const { audienceId, selectedDistributionContacts, surveyId } = this.props;

		this.delete(
			surveyId,
			audienceId,
			selectedDistributionContacts.map((contact) => contact.distributionContactId),
		);
	};

	public handleAction = (value?: any) => {
		const {
			selectedDistributionContacts,
			surveyId,
			audienceId,
			ExportDistributionContactList,
			ToggleReopenWarningModal,
			ToggleResendWarningModal,
			ToggleResetWarningModal,
			ToggleDeleteDistributionContactsWarningModal,
		} = this.props;

		if (selectedDistributionContacts && selectedDistributionContacts.length > 0) {
			switch (value) {
				case SurveyParticipantsMenuActions.Reset:
					ToggleResetWarningModal(true);
					break;
				case SurveyParticipantsMenuActions.Reopen:
					ToggleReopenWarningModal(true);
					break;
				case SurveyParticipantsMenuActions.Resend:
					ToggleResendWarningModal(true);
					break;
				case SurveyParticipantsMenuActions.Delete:
					ToggleDeleteDistributionContactsWarningModal(true);
					break;
				case SurveyParticipantsMenuActions.Export:
					ExportDistributionContactList(surveyId, audienceId, selectedDistributionContacts);
					break;
			}
		}
	};

	public setDisplayDuplicateEmailsState = (value?: any) => {
		const { SetDisplayDuplicateEmailsState } = this.props;
		if (value) {
			BrowserHistoryHelper.setQueryStringForCurrentLocation(Constants.SHOW_ONLY_DUPLICATES_QUERY_STRING);
		} else {
			BrowserHistoryHelper.setQueryStringForCurrentLocation('');
		}

		SetDisplayDuplicateEmailsState(value);

		return true;
	};

	public getshowDuplicatesOnlyQueryStringValue() {
		return BrowserHistoryHelper.getQueryStringValueFromCurrentLocation('showOnlyDuplicates') === 'true';
	}
}
